// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import { formatNumber, roundNumber } from '../../plugins/general';

// Hooks
import { useOverflowTooltip } from '../../hooks/useOverflowTooltip/useOverflowTooltip';

// Components
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';
import { Tooltip } from '../../layout/elements/Tooltip';
import { Col } from '../../layout/elements/Col';
import { Row } from '../../layout/elements/Row';

// Component
import { CardEquipmentProps } from './CardEquipment.types';
import {
  CardEquipmentComponent,
  ContentContainer,
  ContentRegisterModels,
  EquipmentIndicators,
  ImageEquipment,
  NameEquipmentContainer,
} from './CardEquipment.style';

function CardEquipment({
  className,
  imgSrc,
  onClick,
  registeredModels = 0,
  registeredUnits = 0,
  testId,
  title,
}: CardEquipmentProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const {
    containerRef: equipmentNameContainerRef,
    contentRef: equipmentNameContentRef,
    isTooltipVisible: renderEquipmentNameTooltip,
  } = useOverflowTooltip({
    overflowCheckTriggers: title,
  });

  const formattedRegisteredModelsCount: string = useMemo((): string => formatNumber(
    roundNumber(registeredModels),
  ), [registeredModels]);

  const formattedRegisteredUnitsCount: string = useMemo((): string => formatNumber(
    roundNumber(registeredUnits),
  ), [registeredUnits]);

  const renderEquipmentName = useMemo((): JSX.Element => (
    <NameEquipmentContainer ref={equipmentNameContainerRef}>
      <ContentContainer ref={equipmentNameContentRef}>
        <Paragraph weight={ParagraphWeight.bold} preventOverflow>
          {title}
        </Paragraph>
      </ContentContainer>
    </NameEquipmentContainer>
  ), [title, equipmentNameContainerRef, equipmentNameContentRef]);

  return (
    <CardEquipmentComponent
      className={className}
      data-testid={testId}
      onClick={onClick}
    >
      <ImageEquipment alt={title} src={imgSrc} />
      {
        renderEquipmentNameTooltip
          ? <Tooltip text={title}>{renderEquipmentName}</Tooltip>
          : renderEquipmentName
      }
      <EquipmentIndicators>
        <Row>
          <Col cols={16}>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.regular}
            >
              {t('component.cardEquipment.registeredModels')}
            </Paragraph>
          </Col>
          <Col cols={8}>
            <ContentRegisterModels>
              <Paragraph
                align={ParagraphAlign.right}
                size={ParagraphSize.sm}
                weight={ParagraphWeight.bold}
              >
                {formattedRegisteredModelsCount}
              </Paragraph>
            </ContentRegisterModels>
          </Col>
        </Row>
        <Row>
          <Col cols={16}>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.regular}
            >
              {t('component.cardEquipment.registeredUnits')}
            </Paragraph>
          </Col>
          <Col cols={8}>
            <Paragraph
              align={ParagraphAlign.right}
              size={ParagraphSize.sm}
              weight={ParagraphWeight.bold}
            >
              {formattedRegisteredUnitsCount}
            </Paragraph>
          </Col>
        </Row>
      </EquipmentIndicators>
    </CardEquipmentComponent>
  );
}

export { CardEquipment };
